import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, DollarSign, Eye, BarChart3, Shield, ChevronRight, Play, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { FilmCard } from '../components/FilmCard';
import { PlatformLogos } from '../components/PlatformLogos';
import { PageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: DollarSign,
    title: '70% VOD EARNINGS',
    description: 'For VOD distribution, you keep 70% of all revenue. No hidden fees, no surprise deductions. Just transparent monthly payments.',
  },
  {
    icon: Eye,
    title: 'THEATRICAL & STREAMING',
    description: 'We act as your sales agent to pursue theatrical distribution and major streaming platforms like Netflix first. If that path isn\'t right for your film, we move to VOD.',
  },
  {
    icon: BarChart3,
    title: 'TRANSPARENT REPORTING',
    description: 'Monthly detailed reports showing exactly where your views and revenue are coming from. No black boxes.',
  },
  {
    icon: Shield,
    title: 'NO HIDDEN COSTS',
    description: 'We believe in filmmaker-first economics. What you see is what you get—30% commission on VOD, competitive rates for sales representation.',
  },
];

export default function Home() {
  const [films, setFilms] = useState([]);
  const [flagshipFilm, setFlagshipFilm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [filmsRes, flagshipRes] = await Promise.all([
          axios.get(`${API}/films`),
          axios.get(`${API}/films/flagship`)
        ]);
        setFilms(filmsRes.data);
        setFlagshipFilm(flagshipRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen">
      <PageSEO page="home" />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_1e9a34c5-062e-4920-9a11-338f797da342/artifacts/zq4i9nki_IMG_0955.jpg"
            alt="Older Gods film production clapperboard - UK film distribution by LBR Distribution"
            className="w-full h-full object-cover object-right"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/50 to-neutral-950/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/70 via-neutral-950/40 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-6">
              FILM DISTRIBUTION REIMAGINED
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight uppercase leading-none text-white mb-6">
              YOUR FILM.<br />
              <span className="text-orange-500">YOUR PROFIT.</span>
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed mb-10 max-w-xl">
              We get your film on streaming platforms ourselves or act as a sales agent to connect you with major distributors. When it comes to VOD, you keep 70% of earnings. Transparent. Simple. Revolutionary.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/submit"
                data-testid="hero-submit-btn"
                className="bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em] flex items-center justify-center gap-3"
              >
                SUBMIT YOUR FILM
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/distribution"
                data-testid="hero-learn-btn"
                className="bg-transparent border border-white/20 hover:border-white text-white font-sans py-4 px-8 uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-3"
              >
                HOW IT WORKS
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-orange-500 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* OLDER GODS - Flagship Film Feature */}
      {flagshipFilm && (
        <section className="relative py-0 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0">
            <img
              src={flagshipFilm.header_url || flagshipFilm.poster_url}
              alt={flagshipFilm.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/80 to-neutral-950" />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/60 to-transparent" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Film Info */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4">
                  NOW STREAMING WORLDWIDE
                </span>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-4">
                  {flagshipFilm.title}
                </h2>
                <p className="text-orange-500 text-xl font-medium italic mb-6">
                  A Lovecraftian descent into madness
                </p>
                <p className="text-neutral-300 text-lg leading-relaxed mb-6">
                  {flagshipFilm.synopsis}
                </p>
                
                {/* Awards */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {flagshipFilm.awards?.slice(0, 2).map((award, index) => (
                    <span key={index} className="bg-orange-600/20 border border-orange-500/30 text-orange-400 text-sm px-4 py-2">
                      {award}
                    </span>
                  ))}
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap gap-4">
                  <a
                    href={flagshipFilm.trailer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="flagship-trailer-btn"
                    className="flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all"
                  >
                    <Play size={18} fill="white" />
                    WATCH TRAILER
                  </a>
                  <Link
                    to="/films/older-gods"
                    data-testid="flagship-details-btn"
                    className="flex items-center gap-3 border border-white/20 hover:border-white text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all"
                  >
                    LEARN MORE
                    <ArrowRight size={18} />
                  </Link>
                </div>

                {/* Platforms */}
                <div className="mt-8 pt-8 border-t border-white/10">
                  <span className="text-neutral-500 text-sm uppercase tracking-wider block mb-4">Available on</span>
                  <div className="flex flex-wrap gap-4">
                    {flagshipFilm.platforms?.map((platform) => (
                      <span key={platform} className="text-white/60 font-display text-lg uppercase tracking-wider">
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Poster */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="hidden lg:block"
              >
                <Link to="/films/older-gods">
                  <img
                    src={flagshipFilm.poster_url}
                    alt={flagshipFilm.title}
                    className="w-full max-w-md mx-auto aspect-[2/3] object-cover shadow-2xl hover:scale-105 transition-transform duration-500"
                  />
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Platforms Section */}
      <section className="py-24 md:py-32 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              DISTRIBUTION PARTNERS
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-16">
              YOUR FILM ON MAJOR PLATFORMS IN 90 DAYS
            </h2>
          </motion.div>
          <PlatformLogos />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              WHY CHOOSE US
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white">
              REVOLUTIONISING FILM DISTRIBUTION
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 border border-white/10 bg-neutral-900/50 hover:bg-neutral-900 transition-colors group"
              >
                <feature.icon className="w-10 h-10 text-orange-500 mb-6" />
                <h3 className="font-display text-xl tracking-wider text-white mb-4 uppercase">{feature.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Catalogue Section */}
      <section className="py-24 md:py-32 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                OUR CATALOGUE
              </span>
              <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white">
                DISTRIBUTED FILMS
              </h2>
            </motion.div>
            <Link
              to="/films"
              data-testid="view-all-films-btn"
              className="hidden md:flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors font-medium"
            >
              VIEW ALL
              <ChevronRight size={20} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[2/3] bg-neutral-900 animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6">
              {films.slice(0, 4).map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          )}

          <Link
            to="/films"
            className="md:hidden flex items-center justify-center gap-2 text-orange-500 hover:text-orange-400 transition-colors font-medium mt-8"
          >
            VIEW ALL FILMS
            <ChevronRight size={20} />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-neutral-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1632670535530-aaf6e90042ca?w=1920&q=80"
            alt="Film production equipment - professional film distribution services UK"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-6xl tracking-tight uppercase text-white mb-6">
              READY TO SHARE YOUR STORY?
            </h2>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
              Join the revolution. Get your film in front of global audiences while keeping the profits you deserve.
            </p>
            <Link
              to="/submit"
              data-testid="cta-submit-btn"
              className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-10 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
            >
              SUBMIT YOUR FILM TODAY
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
